import { motion } from "framer-motion";
import { Zap, Building2, Home, ShieldCheck, ArrowUpRight, MessageCircle } from "lucide-react";
import { useLang } from "../i18n";
import { Reveal, Eyebrow } from "./common";
import { wa, DEFAULT_MSG_PT, DEFAULT_MSG_EN, scrollToId, IMAGES } from "../lib/site";

const icons = [Zap, Home, ShieldCheck];

export default function Services() {
    const { lang, t } = useLang();
    const msg = lang === "pt" ? DEFAULT_MSG_PT : DEFAULT_MSG_EN;

    return (
        <section id="servicos" className="relative py-24 md:py-32">
            <div className="max-w-7xl mx-auto px-5 md:px-10">
                <div className="grid lg:grid-cols-[1fr_auto] gap-8 items-end mb-14">
                    <Reveal>
                        <Eyebrow>{t.services.eyebrow}</Eyebrow>
                        <h2 className="mt-4 font-display font-extrabold tracking-tight text-3xl sm:text-4xl lg:text-5xl leading-[1.05]">
                            {t.services.title}
                        </h2>
                        <p className="mt-4 max-w-xl text-slate-400">{t.services.sub}</p>
                    </Reveal>
                    <Reveal delay={0.15} className="hidden lg:block">
                        <div className="clip-notch border border-white/10 w-72 h-40 relative overflow-hidden">
                            <img
                                src="/obras/obra-5.jpg"
                                alt="Equipe ANG em campo"
                                className="w-full h-full object-cover contrast-[1.06] brightness-[0.96]"
                                loading="lazy"
                            />
                            <div className="absolute inset-0 bg-gradient-to-t from-obsidian/90 to-transparent" />
                            <div className="absolute inset-0 bg-volt/10 mix-blend-overlay" />
                            <div className="absolute top-0 right-0 w-10 h-10 border-t-2 border-r-2 border-volt" />
                        </div>
                    </Reveal>
                </div>

                <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-5">
                    {t.services.items.map((item, i) => {
                        const Icon = icons[i];
                        return (
                            <Reveal key={item.title} delay={i * 0.12} className="h-full">
                                <motion.article
                                    whileHover={{ y: -6 }}
                                    transition={{ type: "spring", stiffness: 260, damping: 22 }}
                                    className={`h-full flex flex-col rounded-2xl border bg-surface/70 backdrop-blur-sm p-7 md:p-8 transition-colors ${
                                        item.link3d
                                            ? "border-volt/40 shadow-[0_0_50px_rgba(255,230,0,0.08)]"
                                            : "border-white/10 hover:border-volt/40"
                                    }`}
                                >
                                    <div className="flex items-center justify-between mb-7">
                                        <span className="w-12 h-12 rounded-xl bg-volt/10 border border-volt/25 flex items-center justify-center">
                                            <Icon className="w-5 h-5 text-volt" />
                                        </span>
                                        <span className="font-mono text-[10px] uppercase tracking-[0.25em] text-slate-500">
                                            {item.tag}
                                        </span>
                                    </div>
                                    <h3 className="font-display font-bold text-xl md:text-2xl leading-snug">
                                        {item.title}
                                    </h3>
                                    <p className="mt-3 text-sm text-slate-400 leading-relaxed">
                                        {item.desc}
                                    </p>
                                    <ul className="mt-6 space-y-2.5 flex-1">
                                        {item.bullets.map((b) => (
                                            <li
                                                key={b}
                                                className="flex items-start gap-2.5 text-sm text-slate-300"
                                            >
                                                <Zap className="w-3.5 h-3.5 text-volt mt-0.5 shrink-0" />
                                                {b}
                                            </li>
                                        ))}
                                    </ul>
                                    {item.link3d && (
                                        <button
                                            data-testid="services-spda-3d-link"
                                            onClick={() => scrollToId("spda")}
                                            className="mt-7 inline-flex items-center gap-2 font-mono text-[11px] uppercase tracking-[0.2em] text-volt hover:gap-3.5 transition-all"
                                        >
                                            3D · SPDA
                                            <ArrowUpRight className="w-4 h-4" />
                                        </button>
                                    )}
                                </motion.article>
                            </Reveal>
                        );
                    })}

                    <Reveal delay={0.35} className="md:col-span-2 lg:col-span-3">
                        <div className="relative overflow-hidden rounded-2xl border border-volt/30 bg-gradient-to-r from-panel to-surface p-8 md:p-10 flex flex-col md:flex-row md:items-center gap-6 justify-between">
                            <div className="absolute -top-20 -right-20 w-72 h-72 bg-volt/10 blur-[90px] rounded-full pointer-events-none" />
                            <div className="relative">
                                <h3 className="font-display font-bold text-xl md:text-2xl flex items-center gap-3">
                                    <Building2 className="w-6 h-6 text-volt" />
                                    {t.services.ctaTitle}
                                </h3>
                                <p className="mt-2 text-sm text-slate-400 max-w-lg">
                                    {t.services.ctaDesc}
                                </p>
                            </div>
                            <a
                                data-testid="services-whatsapp-cta"
                                href={wa(msg)}
                                target="_blank"
                                rel="noopener noreferrer"
                                className="relative inline-flex shrink-0 items-center gap-2.5 bg-volt text-obsidian font-bold px-7 py-4 rounded-full hover:shadow-[0_0_36px_rgba(255,230,0,0.45)] hover:-translate-y-0.5 transition-all"
                            >
                                <MessageCircle className="w-4.5 h-4.5" />
                                {t.services.ctaBtn}
                            </a>
                        </div>
                    </Reveal>
                </div>
            </div>
        </section>
    );
}
